import { Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import MapPopupQRCode from './MapPopupQRCode';
import MapPlantationImagesOnPinpoint from './MapPlantationImagesOnPinpoint';
import { Icon } from '@iconify/react/dist/iconify.js';
import { useEffect, useRef } from 'react';

// Create custom icons based on category
const createCustomIcon = (category) => {
  const iconUrl = category === 'DEPARTMENTAL' 
    ? process.env.PUBLIC_URL + "/assets/images/pinplantyellow.png"
    : process.env.PUBLIC_URL + "/assets/images/pinplant.png";
  
  return L.icon({
    iconUrl: iconUrl,
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41]
  });
};

const MapApiPinpoint = ({ plantation, index }) => {
  const popupRef = useRef();
  
  // This effect will run after the popup is rendered
  useEffect(() => {
    if (popupRef.current) {
      // Force Leaflet to update the popup position
      const popup = popupRef.current;
      if (popup && popup.leafletElement) {
        // Small delay to ensure the popup is fully rendered
        setTimeout(() => {
          popup.leafletElement.update();
        }, 100);
      }
    }
  }, [plantation]);

  // Early return conditions - must come after all hooks
  if (!plantation.location || !plantation.location.coordinates || plantation.location.coordinates.length !== 2) {
    return null;
  }
  
  const [lng, lat] = plantation.location.coordinates;
  
  if (typeof lat !== 'number' || typeof lng !== 'number' || isNaN(lat) || isNaN(lng)) {
    return null;
  }
  
  const position = [lat, lng];
  
  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  return (
    <>
      <style>
        {`
          .custom-popup {
            border-radius: 14px !important;
            box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15) !important;
            overflow: hidden !important;
            width: 280px !important;
            background: #ffffff !important;
            font-family: 'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif !important;
            border: 1px solid #e2e8f0;
          }
          
          .custom-popup .leaflet-popup-content-wrapper {
            padding: 0 !important;
            background: transparent !important;
            border-radius: 14px !important;
          }
          
          .custom-popup .leaflet-popup-content {
            margin: 0 !important;
            width: 100% !important;
            max-height: 280px !important;
            overflow-y: auto !important;
            scrollbar-width: thin !important;
            scrollbar-color: #C0C0C0 #edf2f7 !important;
          }
          
          .custom-popup .leaflet-popup-content::-webkit-scrollbar {
            width: 5px !important;
          }
          
          .custom-popup .leaflet-popup-content::-webkit-scrollbar-track {
            background: #edf2f7 !important;
            border-radius: 4px !important;
          }
          
          .custom-popup .leaflet-popup-content::-webkit-scrollbar-thumb {
            background: #C0C0C0 !important;
            border-radius: 4px !important;
          }
          
          .popup-headermap {
            background: #81C784;
            color: #ffffff;
            padding: 14px 16px 12px;
            position: relative;
          }
          
          .popup-titlemap {
            margin: 0 0 8px 0;
            font-size: 16px;
            font-weight: 700;
            line-height: 1.2;
            color: #000000ff !important;
            text-shadow: none !important;
          }
          
          .popup-badges {
            display: flex;
            gap: 8px;
            align-items: center;
          }
          
          .popup-badge {
            background: rgba(255, 255, 255, 0.8);
            backdrop-filter: blur(4px);
            padding: 4px 10px;
            border-radius: 12px;
            font-size: 11px;
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: 0.3px;
            border: 1px solid rgba(0, 0, 0, 0.2);
            color: #333333;
          }
          
          .popup-year {
            background: rgba(255, 255, 255, 0.6);
            padding: 4px 10px;
            border-radius: 12px;
            font-size: 11px;
            font-weight: 600;
            color: #333333;
          }
          
          .popup-content {
            padding: 16px;
            background: #fafdf8;
          }
          
          .plantation-id {
            display: flex;
            align-items: center;
            margin: 0 0 16px 0;
            font-size: 12px;
            font-weight: 600;
            color: #333333;
            padding: 10px 12px;
            background: white;
            border-radius: 10px;
            box-shadow: 0 2px 6px rgba(0,0,0,0.05);
            border: 1px solid #e8f5e9;
          }
          
          .info-section {
            margin-bottom: 16px;
            background: white;
            border-radius: 10px;
            box-shadow: 0 2px 6px rgba(0,0,0,0.05);
            border: 1px solid #e8f5e9;
            overflow: hidden;
          }
          
          .section-header {
            font-size: 14px;
            font-weight: 700;
            color: #2E7D32;
            margin: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 8px;
            padding: 12px 16px;
            background: #f8fdf8;
            border-bottom: 1px solid #e8f5e9;
            white-space: nowrap;
            text-align: center;
          }
          
          .info-list {
            display: flex;
            flex-direction: column;
            gap: 10px;
            background: white;
            padding: 12px;
          }
          
          .info-item {
            display: flex;
            justify-content: space-between;
            align-items: center;
            font-size: 13px;
            padding: 8px 10px;
            background: #fafdf8;
            border-radius: 8px;
            transition: all 0.2s ease;
            border: 1px solid #f1f8f1;
          }
          
          .info-item:hover {
            background: #f1f8f1;
            transform: translateY(-1px);
          }
          
          .info-label {
            color: #333333;
            font-weight: 600;
            display: flex;
            align-items: center;
            gap: 6px;
            font-size: 12px;
          }
          
          .info-value {
            color: #2E7D32;
            font-weight: 700;
            text-align: right;
            max-width: 140px;
            overflow: hidden;
            text-overflow: ellipsis;
            font-size: 13px;
          }
          
          .images-section {
            margin-top: 16px;
            padding-top: 16px;
            border-top: 1px dashed #c8e6c9;
          }
          
          .section-title {
            font-size: 13px;
            font-weight: 700;
            color: #2E7D32;
            margin-bottom: 10px;
            display: flex;
            align-items: center;
            gap: 6px;
          }
          
          .icon-green {
            color: #4CAF50;
          }
          
          .icon-location {
            color: #2196F3;
          }
          
          .icon-plant {
            color: #4CAF50;
          }
          
          /* ENHANCED QR CODE CENTERING STYLES */
          .qr-code-container {
            display: flex !important;
            justify-content: center !important;
            align-items: center !important;
            margin: 0 auto 16px auto !important;
            padding: 12px !important;
            background: white;
            border-radius: 10px;
            box-shadow: 0 2px 6px rgba(0,0,0,0.05);
            border: 1px solid #e8f5e9;
            text-align: center !important;
            width: 100% !important;
          }
          
          .qr-code-wrapper {
            display: flex !important;
            justify-content: center !important;
            align-items: center !important;
            width: 100% !important;
            text-align: center !important;
          }
          
          /* Target the QR code component directly */
          .qr-code-container > * {
            margin: 0 auto !important;
            display: block !important;
          }
          
          /* If the QR code component has its own wrapper */
          .qr-code-container canvas,
          .qr-code-container img,
          .qr-code-container svg {
            display: block !important;
            margin: 0 auto !important;
          }
          
          /* Additional centering for any nested elements */
          .qr-code-wrapper > * {
            margin: 0 auto !important;
            text-align: center !important;
          }
        `}
      </style>
      <Marker 
        position={position} 
        icon={createCustomIcon(plantation.category)}
      >
        <Popup 
          className="custom-popup" 
          closeButton={false}
          ref={popupRef}
        >
          <div className="popup-header">
            <div className="popup-titlemap">Plantation Details</div>
            <div className="popup-badges">
              <span className="popup-badge">{plantation.category}</span>
              <span className="popup-year">{formatDate(plantation.plantationDate) || 'N/A'}</span>
            </div>
          </div>
          
          <div className="popup-content">
            {/* QR Code Section - Enhanced Centering */}
            {plantation._id && (
              <div className="qr-code-container">
                <div className="qr-code-wrapper">
                  <MapPopupQRCode plantation={plantation} />
                  <span>ID: {plantation.plantationKey}</span>
                </div>
              </div>
            )}
            
            {/* Location Information Section */}
            <div className="info-section">
              <div className="section-header">
                <Icon icon="mdi:map-marker" width="16" className="icon-location" />
                Location Information
              </div>
              <div className="info-list">
                {[
                  { 
                    icon: 'mdi:map', 
                    label: plantation.province === "Islamabad Capital Territory" ? 'Capital' : 'Province', 
                    value: plantation.province 
                  },
                  { icon: 'mdi:map-outline', label: 'District', value: plantation.district },
                  { icon: 'mdi:map-marker', label: 'Tehsil', value: plantation.tehsil },
                ]
                  .filter(item => item.value)
                  .map((item, index) => (
                    <div key={index} className="info-item">
                      <span className="info-label">
                        <Icon icon={item.icon} width="14" className="icon-location" />
                        {item.label}
                      </span>
                      <span className="info-value">{item.value}</span>
                    </div>
                  ))}
              </div>
            </div>
            
            {/* Plant Information Section */}
            <div className="info-section">
              <div className="section-header">
                <Icon icon="mdi:tree" width="16" className="icon-plant" />
                Plant Information
              </div>
              <div className="info-list">
                {[
                    { icon: 'mdi:tree', label: 'Plants', value: plantation.numberOfPlants },
                    { icon: 'mdi:leaf', label: 'Species', value: plantation.speciesName },
                    { 
                      icon: 'mdi:home', 
                      label: 'Site', 
                      value: plantation.category === 'DEPARTMENTAL' ? plantation.siteName : null 
                    },
                    { 
                      icon: 'mdi:home', 
                      label: 'Site Type', 
                      value: plantation.category === 'DEPARTMENTAL' ? plantation.siteType : null 
                    },
                    { icon: 'mdi:sprout', label: 'Plant Type', value: plantation.plantType },
                    { 
                      icon: 'mdi:provider', 
                      label: 'Program', 
                      value: plantation.category === 'DEPARTMENTAL' ? plantation.plantationProgram : null 
                    },
                    { icon: 'mdi:provider', label: 'Plantation Type', value: plantation.plantationType },
                    {  icon: 'mdi:megaphone',  label: 'Compaign', value: plantation.plantationCampaign },
                  ]
                  .filter(item => item.value)
                  .map((item, index) => (
                    <div key={index} className="info-item">
                      <span className="info-label">
                        <Icon icon={item.icon} width="14" className="icon-plant" />
                        {item.label}
                      </span>
                      <span className="info-value">{item.value}</span>
                    </div>
                  ))}
              </div>
            </div>
            
            {Array.isArray(plantation.images) && plantation.images.length > 0 && (
              <div className="images-section">
                <div className="section-title">
                  <Icon icon="mdi:image" width="16" className="icon-green" />
                  Plantation Images
                </div>
                <MapPlantationImagesOnPinpoint images={plantation.images} />
              </div>
            )}
          </div>
        </Popup>
      </Marker>
    </>
  );
};

export default MapApiPinpoint;